package com.fantasytenis.web.hooks

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

/**
 * State for panel resize functionality.
 */
data class ResizeState(
    val width: Int,
    val isResizing: Boolean = false,
    val isHovering: Boolean = false
)

/**
 * Hook for handling resizable panel drag interactions.
 *
 * Supports both mouse and touch events for desktop and tablet.
 *
 * @param initialWidth Initial panel width in pixels
 * @param minWidth Minimum allowed width
 * @param maxWidth Maximum allowed width
 * @param onWidthChange Called when width changes (after drag ends, for persistence)
 * @return ResizeHandlers with state and event handlers
 */
@Composable
fun useResizablePanel(
    initialWidth: Int = 420,
    minWidth: Int = 320,
    maxWidth: Int = 600,
    onWidthChange: (Int) -> Unit = {}
): ResizeHandlers {
    // State
    var state by remember { mutableStateOf(ResizeState(width = initialWidth)) }
    var startX by remember { mutableStateOf(0.0) }
    var startWidth by remember { mutableStateOf(initialWidth) }

    // Calculate effective max width based on viewport
    val effectiveMaxWidth = remember(state.isResizing) {
        val viewportLimit = (window.innerWidth * 0.7).toInt()
        minOf(maxWidth, viewportLimit)
    }

    // Clean up body styles on unmount
    DisposableEffect(Unit) {
        onDispose {
            document.body?.classList?.remove("is-resizing-panel")
        }
    }

    // Mouse move handler (registered on window during drag)
    val handleMouseMove: (Event) -> Unit = { event ->
        if (state.isResizing) {
            val mouseEvent = event.unsafeCast<MouseEvent>()
            // Dragging left (decreasing clientX) increases width (panel is right-aligned)
            val deltaX = startX - mouseEvent.clientX.toDouble()
            val newWidth = (startWidth + deltaX.toInt()).coerceIn(minWidth, effectiveMaxWidth)
            state = state.copy(width = newWidth)
        }
    }

    // Mouse up handler (registered on window during drag)
    val handleMouseUp: (Event) -> Unit = {
        if (state.isResizing) {
            document.body?.classList?.remove("is-resizing-panel")
            state = state.copy(isResizing = false)
            onWidthChange(state.width)
        }
    }

    // Touch move handler
    val handleTouchMove: (Event) -> Unit = { event ->
        if (state.isResizing) {
            val touchEvent = event.unsafeCast<TouchEvent>()
            val touch = touchEvent.touches.item(0)
            if (touch != null) {
                event.preventDefault() // Prevent page scroll
                val deltaX = startX - touch.clientX.toDouble()
                val newWidth = (startWidth + deltaX.toInt()).coerceIn(minWidth, effectiveMaxWidth)
                state = state.copy(width = newWidth)
            }
        }
    }

    // Touch end handler
    val handleTouchEnd: (Event) -> Unit = {
        if (state.isResizing) {
            document.body?.classList?.remove("is-resizing-panel")
            state = state.copy(isResizing = false)
            onWidthChange(state.width)
        }
    }

    // Register/unregister global event listeners when resizing starts/stops
    DisposableEffect(state.isResizing) {
        if (state.isResizing) {
            window.addEventListener("mousemove", handleMouseMove)
            window.addEventListener("mouseup", handleMouseUp)
            window.addEventListener("touchmove", handleTouchMove, js("{ passive: false }"))
            window.addEventListener("touchend", handleTouchEnd)
            window.addEventListener("touchcancel", handleTouchEnd)
        }

        onDispose {
            window.removeEventListener("mousemove", handleMouseMove)
            window.removeEventListener("mouseup", handleMouseUp)
            window.removeEventListener("touchmove", handleTouchMove)
            window.removeEventListener("touchend", handleTouchEnd)
            window.removeEventListener("touchcancel", handleTouchEnd)
        }
    }

    // Mouse down handler for drag handle
    val handleMouseDown: (Event) -> Unit = { event ->
        val mouseEvent = event.unsafeCast<MouseEvent>()
        event.preventDefault()
        startX = mouseEvent.clientX.toDouble()
        startWidth = state.width
        document.body?.classList?.add("is-resizing-panel")
        state = state.copy(isResizing = true)
    }

    // Touch start handler for drag handle
    val handleTouchStart: (Event) -> Unit = { event ->
        val touchEvent = event.unsafeCast<TouchEvent>()
        val touch = touchEvent.touches.item(0)
        if (touch != null) {
            startX = touch.clientX.toDouble()
            startWidth = state.width
            document.body?.classList?.add("is-resizing-panel")
            state = state.copy(isResizing = true)
        }
    }

    // Hover handlers for visual feedback
    val handleMouseEnter: () -> Unit = {
        if (!state.isResizing) {
            state = state.copy(isHovering = true)
        }
    }

    val handleMouseLeave: () -> Unit = {
        if (!state.isResizing) {
            state = state.copy(isHovering = false)
        }
    }

    // Update width externally (e.g., when loading from preferences)
    val setWidth: (Int) -> Unit = { newWidth ->
        state = state.copy(width = newWidth.coerceIn(minWidth, effectiveMaxWidth))
    }

    return ResizeHandlers(
        state = state,
        onMouseDown = handleMouseDown,
        onTouchStart = handleTouchStart,
        onMouseEnter = handleMouseEnter,
        onMouseLeave = handleMouseLeave,
        setWidth = setWidth
    )
}

/**
 * Resize event handlers to attach to the drag handle element.
 */
data class ResizeHandlers(
    val state: ResizeState,
    val onMouseDown: (Event) -> Unit,
    val onTouchStart: (Event) -> Unit,
    val onMouseEnter: () -> Unit,
    val onMouseLeave: () -> Unit,
    val setWidth: (Int) -> Unit
)
